import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ROLE_LABEL } from "@/lib/constants";
import { Users, Plus, Search, KeyRound, UserX, UserCheck, Pencil, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const ROLE_OPTIONS = [
  "SUPER_ADMIN","ADMIN_KLASIS","KETUA_KLASIS","WAKIL_KETUA","SEKRETARIS_KLASIS","WAKIL_SEKRETARIS",
  "BENDAHARA_KLASIS","WAKIL_BENDAHARA","KOORDINATOR_BIDANG","BP2K",
  "GEMBALA_JEMAAT","SEKRETARIS_JEMAAT","BENDAHARA_JEMAAT","PANITIA","VIEWER_INTERNAL"
];

export default function ManajemenPengguna() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [churches, setChurches] = useState([]);
  const [q, setQ] = useState("");
  const [filterRole, setFilterRole] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tempPw, setTempPw] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "SEKRETARIS_JEMAAT", church_id: null, phone: "" });

  const canManage = ["SUPER_ADMIN", "ADMIN_KLASIS"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([api.get("/users"), api.get("/churches")]);
      setItems(u.data); setChurches(c.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (canManage) load(); else setLoading(false); }, [canManage]);

  const filtered = useMemo(() => items.filter((u) => {
    if (filterRole !== "ALL" && u.role !== filterRole) return false;
    if (q && !`${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, q, filterRole]);

  const churchName = (id) => churches.find((c) => c.id === id)?.short_name || "—";

  const createUser = async () => {
    if (!form.email || !form.name) { toast.error("Email & nama wajib"); return; }
    try {
      const { data } = await api.post("/users", { ...form, church_id: form.church_id || null });
      setTempPw({ email: data.user.email, pw: data.temp_password });
      toast.success("Akun dibuat. Salin password sementara sebelum tutup.");
      setOpenNew(false);
      setForm({ email: "", name: "", role: "SEKRETARIS_JEMAAT", church_id: null, phone: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/users/${editing.id}`, {
        name: editing.name, role: editing.role, church_id: editing.church_id || null, phone: editing.phone,
      });
      toast.success("User diperbarui");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (uid) => {
    try {
      const { data } = await api.patch(`/users/${uid}/toggle-active`);
      toast.success(data.active ? "Akun diaktifkan" : "Akun dinonaktifkan");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetPw = async (uid, email) => {
    if (!window.confirm(`Reset password ${email}? Password lama akan hangus.`)) return;
    try {
      const { data } = await api.post(`/users/${uid}/reset-password`);
      setTempPw({ email: data.email, pw: data.temp_password });
      toast.success("Password direset. Salin sebelum tutup.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Disalin"); };

  if (!canManage) {
    return (
      <AppShell>
        <div className="p-12 text-center border border-dashed border-border rounded-lg">
          <Users className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Hanya Super Admin / Admin Klasis yang dapat mengelola pengguna.</div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Sistem • Admin</div>
          <h1 className="font-heading font-bold text-3xl mt-1">Manajemen Pengguna</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Buat akun baru, ubah role, reset password, aktif/nonaktifkan akun tanpa menyentuh database.</p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="btn-new-user"><Plus className="h-4 w-4 mr-2" /> Akun Baru</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Buat Akun Baru</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nama Lengkap</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="input-user-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} data-testid="input-user-email" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v)=>setForm({...form, role:v})}>
                    <SelectTrigger data-testid="select-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLE_OPTIONS.map(r=><SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Jemaat (opsional)</Label>
                  <Select value={form.church_id || "NONE"} onValueChange={(v)=>setForm({...form, church_id: v === "NONE" ? null : v})}>
                    <SelectTrigger data-testid="select-user-church"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">— Tanpa Jemaat —</SelectItem>
                      {churches.map(c=><SelectItem key={c.id} value={c.id}>{c.short_name || c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>No HP / WhatsApp (opsional, format +62..)</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} placeholder="+6281234567890" data-testid="input-user-phone" /></div>
              <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded-md">Password sementara akan digenerate otomatis dan ditampilkan sekali setelah dibuat.</div>
            </div>
            <DialogFooter><Button onClick={createUser} data-testid="btn-save-user">Buat Akun</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Cari nama atau email…" value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9" data-testid="input-user-search" />
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-56" data-testid="select-filter-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Semua Role</SelectItem>
            {ROLE_OPTIONS.map(r=><SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="p-6 text-sm text-muted-foreground">Memuat…</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left p-3 font-semibold">Nama & Email</th>
                    <th className="text-left p-3 font-semibold">Role</th>
                    <th className="text-left p-3 font-semibold">Jemaat</th>
                    <th className="text-left p-3 font-semibold">Status</th>
                    <th className="text-right p-3 font-semibold">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.id} className="border-t border-border" data-testid={`row-user-${u.id}`}>
                      <td className="p-3">
                        <div className="font-medium">{u.name}</div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                        {u.phone && <div className="text-[0.7rem] text-muted-foreground">📱 {u.phone}</div>}
                      </td>
                      <td className="p-3 text-xs">{ROLE_LABEL[u.role] || u.role}</td>
                      <td className="p-3 text-xs">{u.church_id ? churchName(u.church_id) : "—"}</td>
                      <td className="p-3">
                        <StatusBadge status={u.active ? "success" : "muted"} label={u.active ? "Aktif" : "Nonaktif"} />
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1.5 justify-end">
                          <Button size="sm" variant="ghost" onClick={()=>setEditing({...u})} className="h-8 px-2" data-testid={`btn-edit-user-${u.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={()=>resetPw(u.id, u.email)} className="h-8 px-2" data-testid={`btn-reset-pw-${u.id}`}><KeyRound className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={()=>toggleActive(u.id)} className={`h-8 px-2 ${u.active ? "text-rose-600" : "text-emerald-600"}`} data-testid={`btn-toggle-user-${u.id}`}>
                            {u.active ? <UserX className="h-3.5 w-3.5" /> : <UserCheck className="h-3.5 w-3.5" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={5} className="p-12 text-center text-sm text-muted-foreground">Tidak ada user cocok.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v)=>!v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">{editing.email}</div>
              <div><Label>Nama</Label><Input value={editing.name} onChange={(e)=>setEditing({...editing, name:e.target.value})} data-testid="input-edit-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Role</Label>
                  <Select value={editing.role} onValueChange={(v)=>setEditing({...editing, role:v})}>
                    <SelectTrigger data-testid="select-edit-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLE_OPTIONS.map(r=><SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Jemaat</Label>
                  <Select value={editing.church_id || "NONE"} onValueChange={(v)=>setEditing({...editing, church_id: v === "NONE" ? null : v})}>
                    <SelectTrigger data-testid="select-edit-church"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">— Tanpa Jemaat —</SelectItem>
                      {churches.map(c=><SelectItem key={c.id} value={c.id}>{c.short_name || c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>No HP</Label><Input value={editing.phone || ""} onChange={(e)=>setEditing({...editing, phone:e.target.value})} data-testid="input-edit-phone" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} data-testid="btn-save-edit-user">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp password dialog */}
      <Dialog open={!!tempPw} onOpenChange={(v)=>!v && setTempPw(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>🔑 Password Sementara</DialogTitle></DialogHeader>
          {tempPw && (
            <div className="space-y-3">
              <div className="text-sm">Salin dan sampaikan kepada user via kanal aman. Password ini <strong>hanya ditampilkan sekali</strong>.</div>
              <div className="p-4 rounded-md bg-primary/5 border border-primary/30 flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">Email</div>
                  <div className="font-mono text-sm">{tempPw.email}</div>
                  <div className="text-xs text-muted-foreground mt-2">Password Sementara</div>
                  <div className="font-mono text-lg font-bold text-primary" data-testid="temp-password">{tempPw.pw}</div>
                </div>
                <Button size="sm" variant="outline" onClick={()=>copy(tempPw.pw)} className="rounded-full" data-testid="btn-copy-pw"><Copy className="h-4 w-4 mr-1" /> Salin</Button>
              </div>
              <div className="text-xs text-muted-foreground">User dapat mengganti password ini setelah login pertama kali.</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
